/**

Coded by www.techhighway.ca

*/

import dayjs from "dayjs";
import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDCircularProgress from "components/MDCircularProgress";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import customAlert from "components/MDAlert/customAlert";
import { useCache } from "context";
import PermissionService from "services/permission-service";

function Form() {
  const { action } = useParams();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [headerTitle, setHeaderTitle] = useState("");

  const navigate = useNavigate();
  const { openAlertSB, closeAlertSB, renderAlertSB } = customAlert();
  const [loading, setLoading] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const { cacheItems, setCacheItems } = useCache();
  const [isData, setIsData] = useState(false);

  const [inputs, setInputs] = useState({
    name: "",
    handler: "",
    indent: "",
    display_order: "",
    is_heading: 0,
    is_custom: 0,
    created_at: "",
  });

  const [errors, setErrors] = useState({
    nameError: false,
    handlerError: false,
    indentError: false,
    displayOrderError: false,
  });

  useEffect(() => {
    if (action === "add") {
      setIsData(true);
      return;
    }

    const showPermission = async () => {
      try {
        const data = await PermissionService.showPermission(id);
        setHeaderTitle(data?.name);
        setInputs(data);
        setCreatedBy(
          `${data?.user?.first_name + (data?.user?.middle_name ? ` ${data?.user?.middle_name}` : "")} ${
            data?.user?.last_name
          }`
        );
        setIsData(true);
      } catch (res) {
        setIsData(true);
        if (res.hasOwnProperty("message")) {
          const state = { color: "error", icon: "warning", msg: res.message, title: "Permission" };
          navigate("/permission", { state });
        } else {
          const state = {
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Permission",
          };
          navigate("/permission", { state });
        }
      }
    };
    showPermission();
  }, []);

  const changeHandler = (e) => {
    if (e.target.name == "indent") {
      setErrors({
        indentError: false,
      });
    }
    if (e.target.name == "display_order") {
      setErrors({
        displayOrderError: false,
      });
    }
    setLoading(false);
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = async (e) => {
    setLoading(true);
    e.preventDefault();
  
    if (inputs.name.trim().length < 1) {
      setErrors({ ...errors, nameError: true });
      return;
    }

    if (inputs.handler.trim().length < 1) {
      setErrors({ ...errors, handlerError: true });
      return;
    }
    const indentFormat = /^[0-5]{1}$/;
    if (!inputs.indent.toString().trim().match(indentFormat)) {
      setErrors({ ...errors, indentError: true });
      return;
    }

    const displayOrderFormat = /^[0-9]{1,3}$/;
    if (!inputs.display_order.toString().trim().match(displayOrderFormat)) {
      setErrors({ ...errors, displayOrderError: true });
      return;
    }

    const newPermission = {
      name: inputs.name,
      handler: inputs.handler,
      indent: inputs.indent,
      display_order: inputs.display_order,
      is_heading: inputs.is_heading,
      is_custom: inputs.is_custom,
    };

    if (action === "add") {
      try {
        const response = await PermissionService.addPermission(newPermission);
        setLoading(false);
        if (response) {
          if (cacheItems.hasOwnProperty("permissions")) {
            // Create a new object without the key
            const { permissions, ...rest } = cacheItems; // Destructure to exclude key
            setCacheItems(rest); // Set the new cache items without key
          }
          const state = {
            color: "success",
            icon: "check",
            msg: "Permission Added Successfully!",
            title: "Permission",
          };
          navigate("/permission", { state });
        }
      } catch (res) {
        if (res.hasOwnProperty("message")) {
          openAlertSB({ color: "error", icon: "warning", msg: res.message, title: "Permission" });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        } else {
          openAlertSB({
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Permission",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
        setLoading(false);
      }
    } else {
      try {
        const response = await PermissionService.updatePermission(id, newPermission);
        setLoading(false);
        if (response) {
          if (cacheItems.hasOwnProperty("permissions")) {
            // Create a new object without the key
            const { permissions, ...rest } = cacheItems; // Destructure to exclude key
            setCacheItems(rest); // Set the new cache items without key
          }
          openAlertSB({
            color: "success",
            icon: "check",
            msg: "Permission Updated Successfully!",
            title: "Permission",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
      } catch (res) {
        if (res.hasOwnProperty("message")) {
          openAlertSB({ color: "error", icon: "warning", msg: res.message, title: "Permission" });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        } else {
          openAlertSB({
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Permission",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
        setLoading(false);
      }
    }

    return () => {
      setInputs({
        name: "",
        handler: "",
        indent: "",
        display_order: "",
        is_heading: "",
        is_custom: "",
      });

      setErrors({
        nameError: false,
        handlerError: false,
        indentError: false,
        displayOrderError: false,
      });
    };
  };

  return (
    <>
      <DashboardLayout
        breadcrumbsTitle={action === "add" ? "Add Permission" : `Edit Permission : ${headerTitle}`}
        isNotFound={inputs?.id && inputs?.id > 0 || action === 'add' ? false : true}
        isData={isData}
      >
        <MDBox py={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <Grid container>
              <Grid item md={6} xs={12} px={3}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Name"
                    fullWidth
                    value={inputs.name}
                    name="name"
                    inputProps={{ maxLength: 50 }}
                    onChange={changeHandler}
                    error={errors.nameError}
                    required
                  />
                </MDBox>
              </Grid>
              <Grid item md={6} xs={12} px={3}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Handler"
                    fullWidth
                    name="handler"
                    value={inputs.handler}
                    inputProps={{ maxLength: 100 }}
                    onChange={changeHandler}
                    error={errors.handlerError}
                    required
                  />
                </MDBox>
              </Grid>
              <Grid item md={6} xs={12} px={3}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Indent"
                    fullWidth
                    name="indent"
                    value={inputs.indent}
                    onChange={changeHandler}
                    error={errors.indentError}
                    helperText={errors.indentError ? "Please enter a valid number." : ""}
                    required
                  />
                </MDBox>
              </Grid>
              <Grid item md={6} xs={12} px={3}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Display Order"
                    fullWidth
                    name="display_order"
                    value={inputs.display_order}
                    onChange={changeHandler}
                    error={errors.displayOrderError}
                    helperText={errors.displayOrderError ? "Please enter a valid number." : ""}
                    required
                  />
                </MDBox>
              </Grid>
              {action === "edit" && (
                <>
                  <Grid item md={6} xs={12} px={3}>
                    <MDBox mb={2}>
                      <MDInput
                        type="text"
                        label="Created By"
                        fullWidth
                        name=""
                        value={createdBy}
                        inputProps={{ maxLength: 50 }}
                        readOnly
                      />
                    </MDBox>
                  </Grid>
                  <Grid item md={6} xs={12} px={3}>
                    <MDBox mb={2}>
                      <MDInput
                        type="text"
                        label="Created At"
                        fullWidth
                        value={dayjs(inputs.created_at).format("DD-MM-YYYY HH:mm A")}
                        inputProps={{ maxLength: 50 }}
                        readOnly
                      />
                    </MDBox>
                  </Grid>
                </>
              )}
              <Grid item md={10} xs={8} />
              <Grid item md={1} xs={2}>
                <MDBox mr={2}>
                  <MDButton
                    component={Link}
                    to="/permission"
                    variant="gradient"
                    color="primary"
                    fullWidth
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item md={1} xs={2}>
                <MDBox mr={3}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <MDCircularProgress
                        size={24}
                        sx={{
                          position: "absolute",
                        }}
                      />
                    ) : action === "add" ? (
                      "Save"
                    ) : (
                      "Update"
                    )}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </DashboardLayout>
      {renderAlertSB}
    </>
  );
}

export default Form;
